/**
 * Cloud save / cross-device sync — Этап 3 backend (2026-05-25).
 *
 * Frontend часть:
 *   1. `pullStateOnce()` — на app mount fetch server state. Если remote
 *      newer (last_active_ms > local + 5s) — merge'им в локальный store.
 *   2. `pushStateDebounced()` — на любое game state change push на сервер.
 *      Debounce 10s чтобы не флудить (rapid тапы → много изменений → один POST).
 *   3. `pushStateNow()` — synchronous push на visibilitychange→hidden +
 *      beforeunload. Использует keepalive fetch.
 *
 * Gates (skip sync если):
 *   - VITE_API_BASE не задан
 *   - consent !== 'granted' (GDPR — без consent shipping локально only)
 *   - anon_id === fallback (localStorage недоступен)
 *
 * Архитектура:
 *   - Reuse serializeGameState / deserializeGameState из game store
 *   - JSON serialize as string (D1 хранит TEXT), не nested object
 *   - last_active_ms берётся из state.stats.lastActiveAt
 *   - Conflict resolution last-write-wins (server side в endpoint)
 *
 * Failure modes (все graceful):
 *   - Network down → drop push, локальный persist живой
 *   - Server 404 (table не migrated) → log warn, drop
 *   - Server 500 → log warn, drop
 *   - Pull 404 → fresh device, push local на следующий tick
 */
import { canSendTelemetry } from './consent.js';
import { getAnonId } from './anonId.js';
import { getTgIdentity } from './tgAuth.js';
import { composeMultiplierFromState } from '@sai-clicker/game-core';
import { useGameStore } from '../stores/game.js';
import {
  deserializeGameState,
  serializeGameState,
  type SerializedGameState,
} from '../stores/gameStateSerializer.js';
import { ALL_PHRASES, LESSONS_ALPHA, UPGRADES_ALPHA } from '@sai-clicker/content';

/** Debounce окно для push'а — 10s игр'у тапнул → ждём ещё → один POST. */
const PUSH_DEBOUNCE_MS = 10_000;
/**
 * Минимальное расхождение wall clock между local и remote для того
 * чтобы посчитать remote «новее». Защита от мелких clock drift между
 * устройствами / serverless edges.
 */
const REMOTE_NEWER_THRESHOLD_MS = 5_000;
/** Versioning для будущих schema migration'ов client-side. */
const CURRENT_STATE_VERSION = 1;

// 2026-05-26: serialize / deserialize вынесены в `stores/gameStateSerializer.ts`
// — единственная копия используется здесь (cloud sync) и в stores/game.ts
// (localStorage persist). DRY + единая версия для backward compat.

function getApiBase(): string | null {
  const base = import.meta.env.VITE_API_BASE ?? '';
  if (base.length === 0) return null;
  return base.replace(/\/$/, '');
}

function canSync(): boolean {
  if (getApiBase() === null) return false;
  if (!canSendTelemetry()) return false;
  const anonId = getAnonId();
  if (anonId === 'ssr' || anonId === 'no-storage') return false;
  return true;
}

let pushTimer: number | null = null;
let pushInFlight = false;

/**
 * Pull server state on app mount. Идемпотентно — повторные вызовы no-op
 * пока есть in-flight. Если remote newer чем local на > threshold —
 * deserialize + setState в store. Multiplier пересчитывается.
 *
 * Возвращает Promise<void>; вызывается из FlowTrackers на mount.
 */
let pulledOnce = false;
export async function pullStateOnce(): Promise<void> {
  if (pulledOnce) return;
  pulledOnce = true;
  if (!canSync()) return;
  const base = getApiBase();
  if (base === null) return;
  const anonId = getAnonId();
  // 2026-05-26 BUG FIX: capture lastActiveAt в момент НАЧАЛА pull'а.
  // Если игрок успел тапнуть пока request летел — local state
  // прогрессировал. После pull сравниваем НЕ только server vs cached,
  // но и проверяем не сделал ли игрок что-то between start-pull и
  // end-pull. Если local продвинулся — оставляем local (не теряем тапы).
  const pullStartLocalActive = useGameStore.getState().state.stats.lastActiveAt;
  // 2026-05-25 tg_user_id priority: если игрок auth'нулся через TG,
  // server lookup сначала по tg_user_id (cross-device). Если нет TG —
  // fallback на anon_id как раньше.
  const tg = getTgIdentity();
  const tgQuery = tg !== null ? `&tg_user_id=${tg.tg_user_id.toString()}` : '';
  try {
    const res = await fetch(`${base}/state?anon_id=${encodeURIComponent(anonId)}${tgQuery}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      console.warn('[cloud-sync] pull failed:', res.status);
      return;
    }
    const data = (await res.json()) as
      | { found: false }
      | {
          found: true;
          state_json: string;
          state_version: number;
          last_active_ms: number;
        };
    if (!data.found) return; // Fresh device — нечего pull'ить
    if (data.state_version > CURRENT_STATE_VERSION) {
      // Server старше client (e.g. кто-то с новой версией клиента
      // успел сохранить state с новой shape) — не загружаем.
      console.warn(
        '[cloud-sync] server state newer than client schema, ignoring:',
        data.state_version,
      );
      return;
    }
    const localState = useGameStore.getState().state;
    const localLastActive = localState.stats.lastActiveAt;
    if (data.last_active_ms <= localLastActive + REMOTE_NEWER_THRESHOLD_MS) {
      // Local не сильно отстаёт (или впереди) — оставляем local. На
      // следующем debounced push'е сервер получит local.
      return;
    }
    // 2026-05-26 BUG FIX: проверка не тапнул ли игрок между началом
    // pull'а и текущим моментом. Если local продвинулся (lastActive
    // вырос с момента pull start) — local имеет fresh данные которые
    // мы потеряли бы при replace. Skip replace, push'нём local через
    // debounced channel.
    if (localLastActive > pullStartLocalActive) {
      console.info('[cloud-sync] local advanced during pull, skipping replace');
      return;
    }
    // Remote newer — merge. Парсим JSON → deserialize Maps/Sets →
    // пересчитываем multiplier.
    let parsed: SerializedGameState;
    try {
      parsed = JSON.parse(data.state_json) as SerializedGameState;
    } catch {
      console.warn('[cloud-sync] pull state_json parse failed');
      return;
    }
    const restored = deserializeGameState(parsed);
    const multiplier = composeMultiplierFromState({
      state: restored,
      lessonsCatalog: LESSONS_ALPHA,
      allPhrases: ALL_PHRASES,
      upgradesCatalog: UPGRADES_ALPHA,
    });
    // Не трогаем UI-only поля (liveFloatingPhrases / activeActivation /
    // pendingUpgradeShowcase). Они runtime, не пересинхрятся.
    useGameStore.setState({
      state: restored,
      multiplier,
    });
    console.info('[cloud-sync] restored from server, last_active diff:', {
      local: localLastActive,
      remote: data.last_active_ms,
    });
  } catch (err) {
    console.warn('[cloud-sync] pull error:', err);
  }
}

/**
 * Push state на сервер. Не throws. Reuse'ит сериализатор + last_active_ms
 * из state.stats.
 *
 * `keepalive` опция — fetch завершается после navigation (для visibility
 * hidden / beforeunload triggers).
 */
async function pushStateOnce(): Promise<void> {
  if (pushInFlight) return;
  if (!canSync()) return;
  const base = getApiBase();
  if (base === null) return;
  const state = useGameStore.getState().state;
  // Если игрок ни разу не тапнул — last_active_ms всё ещё createInitialState
  // (Date.now() в момент app mount). Push такого «пустого» state даже на
  // первом mount имеет смысл — сервер знает что устройство существует.
  const serialized = serializeGameState(state);
  // Передаём tg_user_id если есть TG identity — server использует
  // её как priority lookup key. Если null/undefined — fallback на anon_id.
  const tg = getTgIdentity();
  const body = JSON.stringify({
    anon_id: getAnonId(),
    state_json: JSON.stringify(serialized),
    state_version: CURRENT_STATE_VERSION,
    last_active_ms: state.stats.lastActiveAt,
    ...(tg !== null ? { tg_user_id: tg.tg_user_id } : {}),
  });

  pushInFlight = true;
  try {
    const res = await fetch(`${base}/state/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
    if (!res.ok) {
      console.warn('[cloud-sync] push failed:', res.status);
      return;
    }
    const result = (await res.json()) as
      | { applied: 'client'; server_last_active_ms: number }
      | { applied: 'noop' }
      | {
          applied: 'server';
          state_json: string;
          state_version: number;
          last_active_ms: number;
        };
    if (result.applied === 'server') {
      // Параллельная сессия на другом устройстве была активнее — берём
      // её state. Условно: игрок на телефоне поиграл 10 минут, потом
      // открыл на компе и затаил, потом снова на телефоне → телефон
      // когда пушит после паузы, может получить server state с компа.
      // Edge case, но handle'им.
      try {
        const parsed = JSON.parse(result.state_json) as SerializedGameState;
        const restored = deserializeGameState(parsed);
        const multiplier = composeMultiplierFromState({
          state: restored,
          lessonsCatalog: LESSONS_ALPHA,
          allPhrases: ALL_PHRASES,
          upgradesCatalog: UPGRADES_ALPHA,
        });
        useGameStore.setState({ state: restored, multiplier });
        console.info('[cloud-sync] push returned newer server state, restored');
      } catch {
        console.warn('[cloud-sync] push response state_json parse failed');
      }
    }
  } catch (err) {
    console.warn('[cloud-sync] push error:', err);
  } finally {
    pushInFlight = false;
  }
}

/**
 * Debounced push. Cancellable таймер — если новое изменение пришло
 * до истечения, рестартуем счёт. Это даёт «rapid тапы → один POST через
 * 10s после последнего» (а не «один POST на каждый тап»).
 */
export function pushStateDebounced(): void {
  if (pushTimer !== null) {
    window.clearTimeout(pushTimer);
  }
  pushTimer = window.setTimeout(() => {
    pushTimer = null;
    void pushStateOnce();
  }, PUSH_DEBOUNCE_MS);
}

/**
 * Immediate push без debounce. Для visibility hidden / beforeunload /
 * explicit «Sync now» buttons. `keepalive: true` уже в pushStateOnce.
 */
export function pushStateNow(): void {
  if (pushTimer !== null) {
    window.clearTimeout(pushTimer);
    pushTimer = null;
  }
  void pushStateOnce();
}
